import React from 'react';
import { alpha, makeStyles, Table, TableBody, TableHead, TableRow, Typography, LinearProgress, Box, Chip } from '@material-ui/core';
import { HeadCell } from '../molecules/Table/HeadCell.jsx';
import { BasicTableCell } from '../molecules/Table/BasicTableCell.jsx';
import { MultiAvatar } from '../molecules/MultiAvatar.jsx';
import { palette } from '../../settings/themes/colors.js';
import { PropTypes } from 'prop-types';
import { Link } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';

const useStyles = makeStyles({
  root: {
    '& th:first-child': {
      width: '380px'
    },
    '& th': {
      width: '90px'
    }
  },
  containerRow: {
    borderBottom: `1px solid ${palette.darkGray20}`
  },
  chip: {
    borderRadius: 8,
    border: `1px solid ${alpha(palette.dimGray, 0.2)}`,
    color: `${palette.white} !important`,
    backgroundColor: palette.captuDataYellow
  },
  chipNotStart: {
    backgroundColor: palette.darkGray20,
    '& *': {
      color: palette.dimGray
    }
  },
  chipInProcess: {
    backgroundColor: palette.captuDataYellow
  },
  chipCompleted: {
    backgroundColor: palette.emerald
  },
  statusCell: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    alignItems: 'flex-start'
  },
  progressBarContainer: {
    display: 'flex',
    gap: 12,
    alignItems: 'center',
    width: '100%',
    flexWrap: 'nowrap'
  },
  progressText: {
    whiteSpace: 'nowrap',
    flexShrink: 0
  },
  progressBar: {
    padding: 1,
    height: 8,
    flex: 1,
    background: palette.silver,
    border: 'none',
    borderRadius: 10
  },
  progressBarNotStart: {
    backgroundColor: palette.darkGray20
  },
  progressBarInProcess: {
    backgroundColor: palette.captuDataYellow
  },
  progressBarCompleted: {
    backgroundColor: palette.emerald
  }
});

export const TableIndicator = ({ indicators = [], projectId, taskId }) => {
  const classes = useStyles();

  const STATUS_PROGRESSES_CLASSNAMES = Object.freeze({
    not_started: classes['progressBarNotStart'],
    in_progress: classes['progressBarInProcess'],
    completed: classes['progressBarCompleted']
  });

  const STATUS_CHIP_CLASSNAMES = Object.freeze({
    not_started: classes['chipNotStart'],
    in_progress: classes['chipInProcess'],
    completed: classes['chipCompleted']
  });

  return (
    <Table className={classes.root}>
      <TableHead>
        <TableRow>
          <HeadCell>
            <FormattedMessage id="common.indicator" />
          </HeadCell>
          <HeadCell>
            <FormattedMessage id="common.variables" />
          </HeadCell>
          <HeadCell>
            <FormattedMessage id="project.indicator.form.lineBase" />
          </HeadCell>
          <HeadCell>
            <FormattedMessage id="project.formulation.form.goal" />
          </HeadCell>
          <HeadCell>
            <FormattedMessage id="project.indicator.table.frequency" />
          </HeadCell>
          <HeadCell>
            <FormattedMessage id="common.responsible" />
          </HeadCell>
          <HeadCell>
            <FormattedMessage id="common.status" />
          </HeadCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {indicators.map(
          (
            {
              name,
              indicatorVariables = [],
              baseLineHuminized,
              goalHuminized,
              measurementFrequency,
              responsible,
              id,
              progressLabel,
              progressPercentage,
              progressStatus
            },
            index
          ) => (
            <TableRow key={index} className={classes.containerRow}>
              <BasicTableCell>
                <Typography>{<Link to={`/projects/${projectId}/formulation/${taskId}/indicators/${id}`}>{name}</Link>}</Typography>
              </BasicTableCell>
              <BasicTableCell>
                {indicatorVariables.map(({ indicatorVariableType = {} }, index) => (
                  <Typography key={index}>
                    {indicatorVariableType.name}
                    {index < indicatorVariables.length - 1 ? ', ' : ''}
                  </Typography>
                ))}
              </BasicTableCell>
              <BasicTableCell>
                <Typography>{baseLineHuminized}</Typography>
              </BasicTableCell>
              <BasicTableCell>
                <Typography>{goalHuminized}</Typography>
              </BasicTableCell>
              <BasicTableCell>
                <Typography>
                  <FormattedMessage
                    id={`project.indicator.details.frequencyMeasure.${measurementFrequency}`}
                    defaultMessage={measurementFrequency}
                  />
                </Typography>
              </BasicTableCell>
              <BasicTableCell>
                <MultiAvatar names={[(responsible || {}).fullName || '']} />
              </BasicTableCell>
              <BasicTableCell>
                <Box className={classes.statusCell}>
                  <Chip label={progressLabel} className={`${classes.chip} ${STATUS_CHIP_CLASSNAMES[progressStatus]}`} />
                  <Box className={classes.progressBarContainer}>
                    <LinearProgress
                      value={progressPercentage}
                      variant="determinate"
                      classes={{
                        root: classes.progressBar,
                        bar: STATUS_PROGRESSES_CLASSNAMES[progressStatus]
                      }}
                    />
                    <Typography className={classes.progressText}>{progressPercentage} %</Typography>
                  </Box>
                </Box>
              </BasicTableCell>
            </TableRow>
          )
        )}
      </TableBody>
    </Table>
  );
};

TableIndicator.propTypes = {
  indicators: PropTypes.array,
  projectId: PropTypes.number,
  taskId: PropTypes.number
};
